import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type BingoCompletion,
  type BingoEventResponse,
  type BingoSpinResponse,
  type BingoState,
  type BingoTile,
} from '@/common/types/bingo';

// Board layout: 6 tiles per row (5 normal + 1 row_reward), 6 rows (5 game + 1 col_rewards + diagonal)
// Index formula: row r, col c → r*6 + c
// row_reward for row r → r*6 + 5
// col_reward for col c → 30 + c
// diagonal_reward → 35

/**
 * Convert API response into internal 36-tile grid.
 * API cells[0..24] map to 5x5 grid positions.
 * API rowRewards[0..4] go into the 6th column.
 * API columnRewards[0..4] go into the 6th row.
 * API diagonalReward goes to position 35.
 */
function buildTilesFromApi(event: BingoEventResponse): BingoTile[] {
  const board = event.board!;
  const tiles: BingoTile[] = [];

  // 5 rows of: 5 normal cells + 1 row reward
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const cell = board.cells[r * 5 + c];
      tiles.push({
        id: r * 6 + c,
        kind: 'normal',
        rewardType: cell.rewardType,
        state: cell.opened ? 'completed' : 'idle',
        value: cell.value,
      });
    }
    // Row reward
    const rowReward = board.rowRewards[r];
    tiles.push({
      id: r * 6 + 5,
      kind: 'row_reward',
      rewardType: rowReward.rewardType,
      state: rowReward.claimed ? 'reward' : 'idle',
      value: rowReward.value,
    });
  }

  // Row 5: column rewards + diagonal
  for (let c = 0; c < 5; c++) {
    const colReward = board.columnRewards[c];
    tiles.push({
      id: 30 + c,
      kind: 'column_reward',
      rewardType: colReward.rewardType,
      state: colReward.claimed ? 'reward' : 'idle',
      value: colReward.value,
    });
  }

  // Diagonal reward at position 35
  const diag = board.diagonalReward;
  tiles.push({
    id: 35,
    kind: 'diagonal_reward',
    rewardType: diag.rewardType,
    state: diag.claimed ? 'reward' : 'idle',
    value: diag.value,
  });

  return tiles;
}

// Lines: only over the 5x5 play area (normal tiles).
// rows: indices r*6 + 0..4, reward at r*6+5
// cols: indices 0..4 * 6 + c, reward at 30+c
// main diagonal: 0,7,14,21,28 → reward at 35
export const BINGO_LINES: number[][] = ((): number[][] => {
  const lines: number[][] = [];
  for (let r = 0; r < 5; r++) {
    lines.push([r * 6, r * 6 + 1, r * 6 + 2, r * 6 + 3, r * 6 + 4]);
  }
  for (let c = 0; c < 5; c++) {
    lines.push([c, 6 + c, 12 + c, 18 + c, 24 + c]);
  }
  lines.push([0, 7, 14, 21, 28]);

  return lines;
})();

// Reward tile index for each line (parallel array to BINGO_LINES)
const LINE_REWARD_INDEX: number[] = [
  5,
  11,
  17,
  23,
  29, // row rewards
  30,
  31,
  32,
  33,
  34, // col rewards
  35, // diagonal reward
];

const NORMAL_TILE_IDS = new Set<number>();
for (let r = 0; r < 5; r++) {
  for (let c = 0; c < 5; c++) {
    NORMAL_TILE_IDS.add(r * 6 + c);
  }
}

function buildEmptyTiles(): BingoTile[] {
  const tiles: BingoTile[] = [];
  for (let i = 0; i < 36; i++) {
    tiles.push({
      id: i,
      kind: 'normal',
      rewardType: 'tgem',
      state: 'idle',
      value: 0,
    });
  }

  return tiles;
}

const initialState: BingoState = {
  eventId: null,
  cardIndex: 0,
  startAt: null,
  endAt: null,
  serverNow: null,
  tiles: buildEmptyTiles(),
  superRewardOptions: [],
  completedLines: [],
  bingosCompleted: 0,
  revealQueue: [],
  lastRevealedTile: null,
  newlyCompletedLines: [],
  isRolling: false,
  boardComplete: false,
  rewardsClaimed: false,
  ballsBalance: 25,
  skipAnimation: false,
  pendingLineRewards: [],
  recentCompletions: [],
};

/**
 * Detect which lines are already completed from API state.
 */
function detectCompletedLines(tiles: BingoTile[]): number[] {
  const completedIds = new Set(
    tiles
      .filter((t) => NORMAL_TILE_IDS.has(t.id) && t.state !== 'idle')
      .map((t) => t.id),
  );
  const completed: number[] = [];
  BINGO_LINES.forEach((line, idx) => {
    if (line.every((id) => completedIds.has(id))) {
      completed.push(idx);
    }
  });

  return completed;
}

const bingoSlice = createSlice({
  name: 'bingo',
  initialState,
  reducers: {
    initFromApi: (state, { payload }: PayloadAction<BingoEventResponse>) => {
      if (!payload.board) return;

      const board = payload.board;
      const tiles = buildTilesFromApi(payload);
      const completedLines = detectCompletedLines(tiles);

      // Mark line tiles as 'reward' state for already completed lines
      completedLines.forEach((lineIdx) => {
        BINGO_LINES[lineIdx].forEach((id) => {
          const t = tiles.find((t) => t.id === id);

          if (t && t.state === 'completed') t.state = 'reward';
        });
        const rewardTile = tiles.find(
          (t) => t.id === LINE_REWARD_INDEX[lineIdx],
        );

        if (rewardTile) rewardTile.state = 'reward';
      });

      state.eventId = payload.id;
      state.cardIndex = board.cardIndex;
      state.startAt = payload.startAt;
      state.endAt = payload.endAt;
      state.serverNow = payload.now;
      state.tiles = tiles;
      state.superRewardOptions = board.superRewardOptions;
      state.completedLines = completedLines;
      state.bingosCompleted = completedLines.length;
      state.boardComplete = tiles
        .filter((t) => NORMAL_TILE_IDS.has(t.id))
        .every((t) => t.state !== 'idle');
      state.revealQueue = [];
      state.lastRevealedTile = null;
      state.newlyCompletedLines = [];
      state.isRolling = false;
      state.rewardsClaimed = false;
      state.pendingLineRewards = [];
      state.recentCompletions = payload.recentCompletions ?? [];
    },

    /**
     * Apply spin results from API.
     * Converts API cellIndex (0-24, 5x5) to internal tile id (6-col grid).
     * All results go into reveal queue — wasAlreadyOpen only prevents
     * changing the tile's visual state, but reward is still shown.
     */
    applySpinResults: (
      state,
      { payload }: PayloadAction<BingoSpinResponse>,
    ) => {
      if (state.isRolling) return;

      const queue = payload.results.map((r) => ({
        tileId: Math.floor(r.cellIndex / 5) * 6 + (r.cellIndex % 5),
        wasAlreadyOpen: r.wasAlreadyOpen,
        rewardType: r.rewardType,
        rewardValue: r.rewardValue,
      }));

      state.ballsBalance = payload.bingoBalls;
      state.boardComplete = payload.cardAdvanced;
      state.revealQueue = queue;
      state.lastRevealedTile = null;
      state.newlyCompletedLines = [];
      state.pendingLineRewards = [];
      state.isRolling = queue.length > 0;
    },

    revealAllFromQueue: (state) => {
      const queue = [...state.revealQueue];
      state.revealQueue = [];
      state.lastRevealedTile = null;

      queue.forEach((item) => {
        state.pendingLineRewards.push({
          rewardType: item.rewardType,
          value: item.rewardValue,
        });

        if (!item.wasAlreadyOpen) {
          const tile = state.tiles.find((t) => t.id === item.tileId);

          if (tile) tile.state = 'completed';
        }
      });

      state.isRolling = false;
    },

    toggleSkipAnimation: (state) => {
      state.skipAnimation = !state.skipAnimation;
    },

    revealNextTile: (state) => {
      const item = state.revealQueue.shift();

      if (!item) return;

      // Always add reward to pending (shown in reward modal)
      state.pendingLineRewards.push({
        rewardType: item.rewardType,
        value: item.rewardValue,
      });

      // Only change tile visual state if it wasn't already open
      if (!item.wasAlreadyOpen) {
        const tile = state.tiles.find((t) => t.id === item.tileId);

        if (tile) tile.state = 'completed';
      }

      state.lastRevealedTile = item.tileId;
    },

    finishRolling: (state) => {
      state.isRolling = false;
    },

    /**
     * Check for newly completed lines after all tiles have been revealed.
     * Marks line tiles as 'reward' and populates newlyCompletedLines for animation.
     */
    checkCompletedLines: (state) => {
      state.newlyCompletedLines = [];

      const completedIds = new Set(
        state.tiles
          .filter((t) => NORMAL_TILE_IDS.has(t.id) && t.state !== 'idle')
          .map((t) => t.id),
      );

      BINGO_LINES.forEach((line, lineIndex) => {
        if (
          !state.completedLines.includes(lineIndex) &&
          line.every((lid) => completedIds.has(lid))
        ) {
          state.completedLines.push(lineIndex);
          state.bingosCompleted += 1;
          line.forEach((lid) => {
            const t = state.tiles.find((t) => t.id === lid);

            if (t && t.state !== 'reward') t.state = 'reward';
          });
          const rewardTile = state.tiles.find(
            (t) => t.id === LINE_REWARD_INDEX[lineIndex],
          );

          if (rewardTile) rewardTile.state = 'reward';
          state.newlyCompletedLines.push(lineIndex);

          if (rewardTile) {
            state.pendingLineRewards.push({
              rewardType: rewardTile.rewardType,
              value: rewardTile.value,
            });
          }
        }
      });
    },

    clearNewlyCompletedLines: (state) => {
      state.newlyCompletedLines = [];
    },

    claimBoardReward: (state) => {
      state.rewardsClaimed = true;
    },

    claimLineRewards: (state) => {
      state.pendingLineRewards = [];
    },

    addCompletion: (state, { payload }: PayloadAction<BingoCompletion>) => {
      state.recentCompletions = [payload, ...state.recentCompletions].slice(
        0,
        6,
      );
    },

    resetBingo: () => ({ ...initialState, tiles: buildEmptyTiles() }),
  },
});

export const {
  initFromApi,
  applySpinResults,
  revealNextTile,
  revealAllFromQueue,
  finishRolling,
  checkCompletedLines,
  clearNewlyCompletedLines,
  toggleSkipAnimation,
  claimBoardReward,
  claimLineRewards,
  addCompletion,
  resetBingo,
} = bingoSlice.actions;
export default bingoSlice.reducer;
